import { useCallback, useState, type FormEvent } from "react";
import type { CreateScheduledEventInput, EventStatus, EventTheme } from "@tih/shared";
import { DEFAULT_FLAME_THEME, isRealEventPoster } from "@tih/shared";
import {
  ApiError,
  adminCreateEvent,
  adminUpdateEvent,
  type UploadedFileDto,
} from "../../../api/client";
import { AdminPosterField } from "../AdminPosterField";
import { EventAccentPicker } from "../EventAccentPicker";
import { EventDetailsFields } from "../EventDetailsFields";
import { useSiteSettings } from "../../../context/SiteSettingsContext";
import { useEventHub } from "../../../context/EventHubContext";
import { extractAccentColors } from "../../../lib/extract-accent-colors";
import {
  defaultEndLocalFromStart,
  fromLocalInput,
  toLocalInput,
} from "../../../lib/event-admin-utils";
import {
  detailsFromJson,
  detailsToJson,
  EMPTY_EVENT_DETAILS,
  type EventDetailsFormState,
} from "../../../lib/event-details-form";
import { useEffect } from "react";

function themeFromExtracted(
  colors: { primary: string; highlight: string; deep: string; background: string },
  prev?: EventTheme,
): EventTheme {
  return {
    primary: colors.primary,
    highlight: colors.highlight,
    deep: colors.deep,
    background: colors.background,
    usePrimary: prev?.usePrimary ?? true,
    useHighlight: prev?.useHighlight ?? true,
    useDeep: prev?.useDeep ?? true,
    useBackground: prev?.useBackground ?? true,
    applyWhenLive: prev?.applyWhenLive ?? true,
  };
}

export function EventOverviewPanel() {
  const hub = useEventHub();
  const { refresh: refreshSite } = useSiteSettings();
  const editingPhase = hub.phase;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [startsLocal, setStartsLocal] = useState("");
  const [endsLocal, setEndsLocal] = useState("");
  const [endsTouched, setEndsTouched] = useState(false);
  const [posterUrl, setPosterUrl] = useState("");
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState<EventStatus>("SCHEDULED");
  const [publicMessage, setPublicMessage] = useState("");
  const [theme, setTheme] = useState<EventTheme>({ ...DEFAULT_FLAME_THEME });
  const [details, setDetails] = useState<EventDetailsFormState>({ ...EMPTY_EVENT_DETAILS });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const runExtraction = useCallback(async (src: string, prev?: EventTheme) => {
    setExtracting(true);
    try {
      const colors = await extractAccentColors(src);
      setTheme((current) => themeFromExtracted(colors, prev ?? current));
    } catch {
      /* keep existing theme */
    } finally {
      setExtracting(false);
    }
  }, []);

  useEffect(() => {
    if (!hub.event) return;
    const ev = hub.event;
    const phase = hub.phase;
    setStartsLocal(toLocalInput(ev.startsAt));
    setEndsLocal(toLocalInput(ev.endsAt));
    setEndsTouched(true);
    setPosterUrl(isRealEventPoster(ev.posterUrl) ? ev.posterUrl : "");
    setPreviewSrc(isRealEventPoster(ev.posterUrl) ? ev.posterUrl : null);
    setTitle(ev.title ?? "");
    setStatus(ev.status ?? "SCHEDULED");
    setPublicMessage(ev.publicMessage ?? "");
    const loadedDetails = detailsFromJson(ev.detailsJson);
    setDetails(loadedDetails);
    setDetailsOpen(
      loadedDetails.useCustomProgram ||
        Boolean(
          loadedDetails.doors ||
            loadedDetails.duration ||
            loadedDetails.venueName ||
            loadedDetails.venueAddress,
        ),
    );
    const loadedTheme = ev.themeJson ?? { ...DEFAULT_FLAME_THEME };
    setTheme(
      phase === "past" ? { ...loadedTheme, applyWhenLive: false } : loadedTheme,
    );
    if (isRealEventPoster(ev.posterUrl) && !ev.themeJson) {
      void runExtraction(ev.posterUrl, DEFAULT_FLAME_THEME);
    }
  }, [hub.event, hub.phase, runExtraction]);

  function onStartsChange(local: string) {
    setStartsLocal(local);
    if (!endsTouched) setEndsLocal(defaultEndLocalFromStart(local));
  }

  function onPosterUploaded(file: UploadedFileDto) {
    const thumb = file.thumbnails.card ?? file.thumbnails.medium ?? file.uploadsUrl;
    setPreviewSrc(thumb);
    void runExtraction(thumb);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!hub.event) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    if (!startsLocal || !endsLocal) {
      setError("Start and end date & time are required");
      setBusy(false);
      return;
    }
    const endsIso = fromLocalInput(endsLocal);
    const startsIso = fromLocalInput(startsLocal);
    if (Date.parse(endsIso) <= Date.parse(startsIso)) {
      setError("End time must be after start time");
      setBusy(false);
      return;
    }
    const detailsJson = detailsToJson(details);
    const hasPoster = isRealEventPoster(posterUrl.trim());
    const themeToSave =
      editingPhase === "past" ? { ...theme, applyWhenLive: false } : theme;

    try {
      const updated = await adminUpdateEvent(hub.event.id, {
        startsAt: startsIso,
        endsAt: endsIso,
        posterUrl: hasPoster ? posterUrl.trim() : "",
        title: title.trim() || null,
        status,
        publicMessage: status === "CANCELLED" ? (publicMessage.trim() || null) : null,
        themeJson: hasPoster ? themeToSave : null,
        detailsJson: detailsJson ?? null,
      });
      hub.setEvent(updated);
      await hub.refreshSummary();
      await refreshSite();
      setSuccess("Event saved.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save event");
    } finally {
      setBusy(false);
    }
  }

  const hint =
    editingPhase === "past"
      ? "Update the archive record (poster, caption, venue copy)."
      : editingPhase === "ongoing"
        ? "This event is on the site right now. Changes apply immediately."
        : "Prepare art and details ahead of time — they go live when this date becomes featured.";

  return (
    <section className="admin-card">
      <h2 className="admin-card-title">Event details</h2>
      <p className="admin-card-foot" style={{ marginTop: 0, marginBottom: 16 }}>
        {hint}
      </p>
      {editingPhase === "past" && (
        <div className="admin-event-context-banner admin-event-context-banner--past">
          Past event — accent colors will not apply on the live site.
        </div>
      )}
      {status === "CANCELLED" && (
        <div className="admin-event-context-banner admin-event-context-banner--cancelled">
          Cancelled — this night will not drive countdown or RSVP. Create the next overnight as a new event; do not edit this row into the next date. Optional public cancellation note shows as an "Overnight cancelled" notice (separate from Settings → General Important Updates).
        </div>
      )}
      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success" style={{ padding: 12, marginBottom: 16 }}>
          {success}
        </div>
      )}
      <form onSubmit={(e) => void onSubmit(e)} className="admin-blog-form">
        <div className="admin-event-form-grid">
          <div className="admin-event-form-poster">
            <AdminPosterField
              value={posterUrl}
              onChange={(url) => {
                setPosterUrl(url);
                setPreviewSrc(null);
              }}
              onUploaded={onPosterUploaded}
              disabled={busy}
              previewSrc={previewSrc}
            />
          </div>
          <div className="admin-event-form-fields">
            <div className="admin-blog-form-fields">
              <div className="admin-form-row">
                <label>
                  <span>Starts (local)</span>
                  <input
                    type="datetime-local"
                    value={startsLocal}
                    onChange={(e) => onStartsChange(e.target.value)}
                    disabled={busy}
                    required
                  />
                </label>
                <label>
                  <span>Ends (local)</span>
                  <input
                    type="datetime-local"
                    value={endsLocal}
                    onChange={(e) => {
                      setEndsTouched(true);
                      setEndsLocal(e.target.value);
                    }}
                    disabled={busy}
                    required
                  />
                </label>
              </div>
              <label>
                <span>Title (caption, optional)</span>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={busy}
                  placeholder="Together In Heaven · Worship Overnight"
                />
              </label>
              <label>
                <span>Status</span>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as EventStatus)}
                  disabled={busy}
                >
                  <option value="SCHEDULED">Scheduled</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </label>
              {status === "CANCELLED" && (
                <label>
                  <span>Public cancellation message</span>
                  <textarea
                    value={publicMessage}
                    onChange={(e) => setPublicMessage(e.target.value)}
                    disabled={busy}
                    rows={4}
                    maxLength={2000}
                    placeholder="Shown as a banner on the public site"
                  />
                  <span className="admin-field-hint">
                    Visitors see this on the coming-soon / home surfaces. RSVP for this night is disabled.
                  </span>
                </label>
              )}
              <EventDetailsFields
                value={details}
                onChange={setDetails}
                disabled={busy}
                open={detailsOpen}
                onToggle={() => setDetailsOpen((v) => !v)}
              />
              <EventAccentPicker
                theme={theme}
                onChange={setTheme}
                onReextract={() => void runExtraction(previewSrc ?? posterUrl)}
                extracting={extracting}
                disabled={busy || !isRealEventPoster(previewSrc ?? posterUrl)}
                lockApplyWhenLive={editingPhase === "past"}
              />
            </div>
          </div>
        </div>
        <div className="admin-blog-form-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </section>
  );
}

export function EventCreatePanel({ onCreated }: { onCreated: (eventId: number) => void }) {
  const { refresh: refreshSite } = useSiteSettings();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startsLocal, setStartsLocal] = useState("");
  const [endsLocal, setEndsLocal] = useState("");
  const [endsTouched, setEndsTouched] = useState(false);
  const [posterUrl, setPosterUrl] = useState("");
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState<EventStatus>("SCHEDULED");
  const [publicMessage, setPublicMessage] = useState("");
  const [theme, setTheme] = useState<EventTheme>({ ...DEFAULT_FLAME_THEME });
  const [details, setDetails] = useState<EventDetailsFormState>({ ...EMPTY_EVENT_DETAILS });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const runExtraction = useCallback(async (src: string, prev?: EventTheme) => {
    setExtracting(true);
    try {
      const colors = await extractAccentColors(src);
      setTheme((current) => themeFromExtracted(colors, prev ?? current));
    } catch {
      /* keep existing theme */
    } finally {
      setExtracting(false);
    }
  }, []);

  function onStartsChange(local: string) {
    setStartsLocal(local);
    if (!endsTouched) setEndsLocal(defaultEndLocalFromStart(local));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    if (!startsLocal || !endsLocal) {
      setError("Start and end date & time are required");
      setBusy(false);
      return;
    }
    const endsIso = fromLocalInput(endsLocal);
    const startsIso = fromLocalInput(startsLocal);
    if (Date.parse(endsIso) <= Date.parse(startsIso)) {
      setError("End time must be after start time");
      setBusy(false);
      return;
    }
    const detailsJson = detailsToJson(details);
    const hasPoster = isRealEventPoster(posterUrl.trim());
    try {
      const body: CreateScheduledEventInput = {
        startsAt: startsIso,
        endsAt: endsIso,
        status,
        ...(status === "CANCELLED"
          ? { publicMessage: publicMessage.trim() || null }
          : { publicMessage: null }),
        ...(hasPoster ? { posterUrl: posterUrl.trim(), themeJson: theme } : {}),
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(detailsJson ? { detailsJson } : {}),
      };
      const created = await adminCreateEvent(body);
      await refreshSite();
      onCreated(created.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create event");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="admin-card">
      <h2 className="admin-card-title">New worship overnight</h2>
      <p className="admin-card-foot" style={{ marginTop: 0, marginBottom: 16 }}>
        Set a future start date to drive the public countdown. Add poster art whenever you are ready.
      </p>
      {error && <div className="form-error">{error}</div>}
      <form onSubmit={(e) => void onSubmit(e)} className="admin-blog-form">
        <div className="admin-event-form-grid">
          <div className="admin-event-form-poster">
            <AdminPosterField
              value={posterUrl}
              onChange={(url) => {
                setPosterUrl(url);
                setPreviewSrc(null);
              }}
              onUploaded={(file) => {
                const thumb =
                  file.thumbnails.card ?? file.thumbnails.medium ?? file.uploadsUrl;
                setPreviewSrc(thumb);
                void runExtraction(thumb);
              }}
              disabled={busy}
              previewSrc={previewSrc}
            />
          </div>
          <div className="admin-event-form-fields">
            <div className="admin-blog-form-fields">
              <div className="admin-form-row">
                <label>
                  <span>Starts (local)</span>
                  <input
                    type="datetime-local"
                    value={startsLocal}
                    onChange={(e) => onStartsChange(e.target.value)}
                    disabled={busy}
                    required
                  />
                </label>
                <label>
                  <span>Ends (local)</span>
                  <input
                    type="datetime-local"
                    value={endsLocal}
                    onChange={(e) => {
                      setEndsTouched(true);
                      setEndsLocal(e.target.value);
                    }}
                    disabled={busy}
                    required
                  />
                </label>
              </div>
              <label>
                <span>Title (caption, optional)</span>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={busy}
                />
              </label>
              <label>
                <span>Status</span>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as EventStatus)}
                  disabled={busy}
                >
                  <option value="SCHEDULED">Scheduled</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </label>
              {status === "CANCELLED" && (
                <label>
                  <span>Public cancellation message</span>
                  <textarea
                    value={publicMessage}
                    onChange={(e) => setPublicMessage(e.target.value)}
                    disabled={busy}
                    rows={4}
                    maxLength={2000}
                    placeholder="Shown as a banner on the public site"
                  />
                  <span className="admin-field-hint">
                    Visitors see this on the coming-soon / home surfaces. RSVP for this night is disabled.
                  </span>
                </label>
              )}
              <EventDetailsFields
                value={details}
                onChange={setDetails}
                disabled={busy}
                open={detailsOpen}
                onToggle={() => setDetailsOpen((v) => !v)}
              />
              <EventAccentPicker
                theme={theme}
                onChange={setTheme}
                onReextract={() => void runExtraction(previewSrc ?? posterUrl)}
                extracting={extracting}
                disabled={busy || !isRealEventPoster(previewSrc ?? posterUrl)}
              />
            </div>
          </div>
        </div>
        <div className="admin-blog-form-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Creating…" : "Create event"}
          </button>
        </div>
      </form>
    </section>
  );
}
